import type { WorkflowStatus, ClosureReason } from '@/api/types/risk-acceptance.types';

export type AssessmentStatus =
  | 'draft'
  | 'issued'
  | 'in_progress'
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'sent_back'
  | 'returned'
  | 'resubmitted'
  | 'rejected'
  | 'expired'
  | 'cancelled';

// Statuses where vendor or tenant still has active work to do.
// Complement-default: anything NOT in this set → Closed & Historical.
// 'reopened' is absent: it is a closed state awaiting re-work assignment.
// 'sent_back' included: legacy pre-VA.2.4 status; live rows exist in production.
export const ACTIVE_STATUSES = new Set<string>([
  'issued',
  'in_progress',
  'submitted',
  'under_review',
  'returned',
  'resubmitted',
  'sent_back',
]);

export type QuestionType = 'multiple_choice' | 'free_text' | 'file_upload' | 'yes_no' | 'scale_1_5' | 'date' | 'number';

// Authoritative scored outcome (matches ScoringService STEP 5: pass/conditional/fail).
export type AssessmentOutcome = 'pass' | 'conditional' | 'fail';

export interface Assessment {
  id: string;
  tenant_id: string;
  vendor_id: string;
  template_id: string;
  title: string;
  status: AssessmentStatus;
  issue_reason: string | null;
  issued_by: string | null;
  issued_at: string | null;
  due_date: string | null;
  submitted_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  approved_at: string | null;
  final_score: number | null;
  pass_threshold_pct: number | null;
  // Authoritative outcome — computed by the api to match ScoringService STEP 5 (pass/conditional/
  // fail; null until scored). Replaces the phantom `passed` (no column, never written).
  outcome?: AssessmentOutcome | null;
  conditional_threshold_pct?: number | null;
  notes_to_vendor: string | null;
  assigned_analyst_id: string | null;
  created_at: string;
  updated_at: string;
  // Joined / computed fields
  vendor_name?: string;
  vendor_tier?: string;
  template_name?: string;
  // P3-B — computed answered/total at the query; null (→ "—", no bar) when there is no real denominator.
  completion_pct?: number | null;
  analyst_name?: string;
  recipient_count?: number;
  recipient_emails?: string[];
  // VA.2.3 freeze fields (Q-VA.2.3-Arch-1 (c))
  score_at_approval?: number | null;
  evidence_frozen_at?: string | null;
  recipient_snapshot?: Record<string, unknown>[] | null;
  // Sprint B1 bypass display fields
  certification_bypass_applied?: boolean;
  bypassed_cert_ids?: string[];
  questions_bypassed_count?: number;
}

export interface AssessmentListFacets {
  status_counts: Partial<Record<string, number>>;
}

export interface AssessmentTemplate {
  id: string;
  tenant_id: string | null;
  name: string;
  description: string | null;
  framework: string | null;
  vendor_type_target: string[];
  version: string;
  // Track 1 — the "rev" in "v{version} rev {content_revision}". On a tenant copy this is the
  // import-snapshot (copied from the hub at import); on a hub row it's the hub-current value.
  content_revision: number;
  is_hub_template: boolean;
  hub_template_id: string | null;
  hub_version_at_import: string | null;
  requires_license: boolean;
  is_ai_generated: boolean;
  total_questions: number;
  total_max_score: number;
  pass_threshold_pct: number;
  conditional_threshold_pct: number;
  download_format: string[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  lifecycle_state?: string;
  // Derived fields — present on specific list endpoints only
  has_ip_attestation?: boolean;
  certification_bypass_eligible?: boolean;
}

export interface AssessmentSection {
  id: string;
  template_id: string;
  name: string;
  description: string | null;
  order_index: number;
  weight_pct: number | null;
  questions: AssessmentQuestion[];
  // computed
  section_score?: number | null;
  max_section_score?: number | null;
}

export interface AssessmentQuestion {
  id: string;
  section_id: string;
  question_text: string;
  question_type: QuestionType;
  options: string[] | null;
  is_required: boolean;
  max_score: number;
  order_index: number;
  // response data (joined on detail fetch)
  response?: AssessmentResponse | null;
}

export interface AssessmentResponse {
  id: string;
  assessment_id: string;
  question_id: string;
  response_text: string | null;
  response_options: string[] | null;
  response_boolean: boolean | null;
  file_url: string | null;
  score_awarded: number | null;
  is_flagged: boolean;
  flag_severity?: FindingSeverity | null;
  analyst_note: string | null;
  is_sent_back: boolean;
  answered_at: string | null;
  vendor_clarification: string | null;
  is_auto_scored?: boolean;
  is_cert_bypassed?: boolean;
}

// The real `finding_severity` enum (5 levels) used by vnd_assessment_responses.flag_severity.
export type FindingSeverity = 'critical' | 'high' | 'medium' | 'low' | 'informational';

// Vendor BI summary — the server-side aggregate (GET /vendors/:id/bi-summary) powering the
// Surface-2 header: cross-assessment findings-by-severity, vendor-scoped status counts, overdue,
// and the completed-assessment trend series.
export interface VendorBiTrendPoint {
  assessment_id: string;
  score: number | string;
  date: string | null;
  template: string | null;
}
export interface VendorBiSummary {
  findings_by_severity: Record<string, number>;
  status_counts: Record<string, number>;
  overdue: number;
  trend: VendorBiTrendPoint[];
}

// One row per (assessment, section), post-scoring — GET /assessments/:id/section-scores.
// NUMERIC columns (score_pct, weighted_contribution) arrive from pg as strings — coerce at use.
export interface SectionScore {
  id: string;
  assessment_id: string;
  section_id: string;
  section_name: string;
  questions_total: number;
  questions_answered: number;
  questions_bypassed: number;
  questions_flagged: number;
  score_awarded: number;
  score_max_possible: number;
  score_pct: number | string;
  weighted_contribution: number | string;
  status: 'pending' | 'pass' | 'conditional' | 'fail';
  calculated_at: string;
}

export interface AssessmentDetail extends Assessment {
  sections: AssessmentSection[];
  vendor?: {
    id: string;
    name: string;
    tier: string;
    status: string;
  };
}

export interface AssessmentHistoryEvent {
  id: string;
  assessment_id: string;
  event_type: string;
  description: string;
  performed_by: string | null;
  performed_by_name: string | null;
  created_at: string;
}

export interface AssessmentFilters {
  search?: string;
  status?: AssessmentStatus;
  tier?: string;
  analyst_id?: string;
  overdue_only?: boolean;
  vendor_id?: string;
  sort?: string;              // whitelisted column key (created_at | due_date | issued_at | status | vendor_name | template_name | score)
  sort_dir?: 'asc' | 'desc';
  outcome?: 'pass' | 'conditional' | 'fail'; // computed-outcome drill-through (scored only)
}

// GET /assessments/overview — tenant-wide operations-workspace aggregate (KPI row + 12-state strip).
// ⚠ No per-assessment risk score, no vendor composite. median_cycle_days is null → render "—".
export interface AssessmentOverview {
  kpis: {
    in_flight: number;
    overdue: number;
    waiting_on_us: number;
    awaiting_scoring: number;
    median_cycle_days: number | null;
    decisions_recorded: number;
  };
  lifecycle_counts: Record<string, number>;
  // Point-in-time pass/conditional/fail over ALL scored assessments (distinct from outcome_trend, the
  // monthly series). Powers the "Assessment outcomes" distribution tile + its drill-through.
  by_outcome: { pass: number; conditional: number; fail: number };
  // P2 charts. cycle_stages: median/p90 days per transition (null when no completed transitions → no bar).
  // outcome_trend: monthly pass/conditional/fail counts (a 0/0/0 month is a GAP, never "0 fail").
  cycle_stages: { stage: string; label: string; median_days: number | null; p90_days: number | null; count: number }[];
  outcome_trend: { buckets: string[]; pass: number[]; conditional: number[]; fail: number[] };
  // P3-A attention — a view of the same table records; one row per assessment, dated reason, severity-first.
  attention: { assessment_id: string; vendor_name: string | null; template_name: string | null; signal: string; label: string; reason: string; severity: number; waiting_days: number }[];
}

// ── Issue Assessment ──────────────────────────────────────────────────────────

export interface IssueAssessmentDto {
  vendor_id: string;
  template_id: string;
  due_date: string;
  assigned_analyst_id?: string;
  notes_to_vendor?: string;
  apply_cert_bypass?: boolean;
}

// ── Return Assessment (VA.2.4) ────────────────────────────────────────────────

export interface ReturnAssessmentDto {
  question_ids: string[];
  message: string;
  returned_reason: string;
}

// ── Portal types ──────────────────────────────────────────────────────────────

export interface PortalAssessment {
  id: string;
  title: string;
  status: AssessmentStatus;
  tenant_name: string;
  due_date: string | null;
  issued_at: string | null;
  submitted_at: string | null;
  answered_count: number;
  total_questions: number;
  required_questions: number;
  answered_required: number;
}

export interface PortalAssessmentDetail {
  id: string;
  title: string;
  status: AssessmentStatus;
  tenant_name: string;
  vendor_name: string;
  due_date: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  allow_bypass: boolean;
  notes_to_vendor: string | null;
  answered_count: number;
  total_questions: number;
  sections: PortalSection[];
  certification_bypass_applied?: boolean;
  bypassed_cert_ids?: string[];
  questions_bypassed_count?: number;
}

export interface PortalSection {
  id: string;
  name: string;
  description: string | null;
  order_index: number;
  questions: PortalQuestion[];
}

export interface PortalQuestion {
  id: string;
  question_text: string;
  guidance_text: string | null;
  question_type: QuestionType;
  options: string[] | null;
  is_required: boolean;
  order_index: number;
  // response (stripped of scoring)
  response_text: string | null;
  response_options: string[] | null;
  response_boolean: boolean | null;
  file_url: string | null;
  vendor_clarification: string | null;
  is_sent_back: boolean;
  analyst_note: string | null;
  answered_at: string | null;
  is_auto_scored?: boolean;
}

export interface PortalCertification {
  id: string;
  cert_type: string;
  issuing_body: string | null;
  cert_number: string | null;
  cert_date: string | null;
  expiry_date: string | null;
  coverage_start: string | null;
  coverage_end: string | null;
  lifecycle_status: 'active' | 'withdrawn';
  scope_description: string | null;
  created_at: string;
}

export interface PortalTeamMember {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  contact_role: string;
  is_portal_admin: boolean;
  invitation_status: string | null;
  created_at: string;
}

// ── Certification ─────────────────────────────────────────────────────────────

// Realigned to the ACTUAL tenant read response (GET /vendors/:id/certifications,
// vendor.controller getCertifications): the API returns the real column names
// (issuing_body, cert_date, document_filename) — NOT issuer/issued_date/document_url —
// and never a cert_number (no such column). Prior keys silently resolved to undefined.
export interface Certification {
  id: string;
  vendor_id: string;
  cert_type: string;
  cert_standard_version?: string | null;
  issuing_body: string | null;
  scope_description?: string | null;
  cert_date: string | null;
  expiry_date: string | null;
  // Coverage window (V987) — vendor-side capture arrives in Sprint 3; NULL until then.
  coverage_start: string | null;
  coverage_end: string | null;
  lifecycle_status: string;
  derived_status?: string;
  document_filename: string | null;
  created_at: string;
  // Layer 2 review fields (CLDS Sub-sprint 3 — joined from vnd_certification_tenant_reviews)
  review_status?: string | null;
  review_notes?: string | null;
  reviewed_at?: string | null;
  reviewer_name?: string | null;
  archived_because?: string | null;
}

// D-RA.9-PE-1: renamed from RiskAcceptance → RiskAcceptanceSummary (TD-RA.3b-3).
// Slim shape used by vendor controller GET /vendors/:id/risk-acceptances.
// RaEntry (risk-acceptance.types.ts) is the canonical comprehensive type.
export interface RiskAcceptanceSummary {
  id: string;
  vendor_id: string;
  title: string;
  description: string | null;
  risk_category: string | null;
  state: string;
  // SS2B-2a: native lifecycle axes (vendor controller returns SELECT *, so both are present at runtime),
  // alongside the retained compat `state` — mirrors RaEntry's workflow_status(7) + closure_reason(7).
  workflow_status: WorkflowStatus;
  closure_reason: ClosureReason | null;
  acceptance_expires_at: string | null;
  approved_by_user_id: string | null;
  approved_at: string | null;
  submitted_by_user_id: string | null;
  created_at: string;
  // RA.5: vendor controller returns SELECT * so parent_acceptance_id is present at runtime
  parent_acceptance_id: string | null;
}
